/**
 * TopicSuggestions — the shared "empty-state → topic in one click" strip
 * used by Image Studio and Video Studio.
 *
 * The user sees a small row of category chips ("Travel · Business · Food …").
 * Clicking a chip pops out a 3-4 line list of ready-to-use topics; clicking a
 * topic fills the Topic textarea and closes the popover. There's also a
 * "Surprise me" chip that picks a random topic across categories.
 */
import React, { useState } from "react";
import { Shuffle, Sparkles, X, ChevronDown } from "lucide-react";
import { TOPIC_CATEGORIES, randomTopic } from "../../data/topicSuggestions";

export default function TopicSuggestions({ lang = "ar", onPick, testIdPrefix = "topic-suggestions" }) {
  const [openKey, setOpenKey] = useState(null);
  const isAr = lang === "ar";

  return (
    <div data-testid={testIdPrefix} className="space-y-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          data-testid={`${testIdPrefix}-random`}
          onClick={() => onPick(randomTopic(lang))}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-sm border border-primary/40 text-primary bg-primary/5 hover:bg-primary/10 transition-colors"
          title={isAr ? "فاجئني بموضوع" : "Surprise me with a topic"}
        >
          <Shuffle className="w-3 h-3" />
          {isAr ? "فاجئني" : "Surprise me"}
        </button>
        {TOPIC_CATEGORIES.map((c) => {
          const active = openKey === c.key;
          return (
            <button
              key={c.key}
              type="button"
              data-testid={`${testIdPrefix}-cat-${c.key}`}
              onClick={() => setOpenKey(active ? null : c.key)}
              className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${
                active
                  ? "border-primary/60 bg-primary/10 text-primary"
                  : "border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}
            >
              {isAr ? c.labelAr : c.labelEn}
              <ChevronDown className={`w-3 h-3 transition-transform ${active ? "rotate-180" : ""}`} />
            </button>
          );
        })}
      </div>

      {openKey && (
        <div
          data-testid={`${testIdPrefix}-panel`}
          className="rounded-sm border border-primary/30 bg-primary/[0.04] p-2 space-y-1"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-primary/90 font-medium">
              {isAr ? "أفكار جاهزة" : "Ready-made ideas"}
            </div>
            <button
              type="button"
              data-testid={`${testIdPrefix}-close`}
              onClick={() => setOpenKey(null)}
              className="p-0.5 hover:bg-muted rounded-sm text-muted-foreground"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="grid gap-1">
            {(TOPIC_CATEGORIES.find((x) => x.key === openKey)?.topics || []).map((t, i) => {
              const text = isAr ? t.ar : t.en;
              return (
                <button
                  key={i}
                  type="button"
                  data-testid={`${testIdPrefix}-topic-${openKey}-${i}`}
                  onClick={() => { onPick(text); setOpenKey(null); }}
                  className="text-xs text-start px-2 py-1.5 rounded-sm hover:bg-primary/10 hover:text-primary transition-colors leading-snug"
                  dir="auto"
                >
                  <Sparkles className="w-3 h-3 inline me-1.5 text-primary/70" />
                  {text}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
